import React, { useId } from 'react';
import PropTypes, { InferProps } from 'prop-types';

function DistributorCard({
  name,
  latitude,
  longitude,
  milesFromSearchPoint,
  distributorType,
  dynamicData,
  getDirectionsLabel,
  websiteLabel,
  profileLabel,
  imagesPath,
  isMetal,
  hideMiles
}: InferProps<typeof DistributorCard.propTypes>) {
  const id = useId();
  const distributorTypeDisplayName = dynamicData.DistributorTypeDisplayName;
  const distributorTypeImageSource = imagesPath
    ? `${imagesPath}${distributorType.toLowerCase().replace(' ', '-')}.png`
    : '/Areas/GAF/images/' +
      distributorType.toLowerCase().replace(' ', '-') +
      '.png';

  const buildGoogleMapsDirectionsURL = (targetAddress: string): string => {
    const googleMapsBaseURL = 'https://www.google.com/maps/dir/?api=1';
    const destinationLocation = `&destination=${encodeURIComponent(
      targetAddress
    )}`;
    return googleMapsBaseURL + destinationLocation;
  };

  const trimmedMiles = Math.ceil(milesFromSearchPoint * 10) / 10;
  let formattedPhoneNumber;

  if (dynamicData.Phone) {
    if (dynamicData.Phone?.[0] === '(') {
      formattedPhoneNumber = dynamicData.Phone;
    } else {
      formattedPhoneNumber =
        dynamicData.Phone.length === 10
          ? `(${dynamicData.Phone.substring(
              0,
              3
            )}) ${dynamicData.Phone.substring(
              3,
              6
            )}-${dynamicData.Phone.substring(6, 10)}`
          : `${dynamicData.Phone.substring(
              0,
              1
            )} (${dynamicData.Phone.substring(
              1,
              4
            )}) ${dynamicData.Phone.substring(
              4,
              7
            )}-${dynamicData.Phone.substring(7, 11)}`;
    }
  }

  const handleMouseEnterAndFocus = () => {
    const customEvent = new CustomEvent('myCustomEvent', {
      detail: {
        latitude,
        longitude
      },
      bubbles: true
    });
    window.dispatchEvent(customEvent);
  };

  return (
    <article
      className="distributor-card"
      aria-labelledby={name}
      onMouseEnter={handleMouseEnterAndFocus}
      onFocus={handleMouseEnterAndFocus}
      tabIndex={0}
    >
      <div className="distributor-card__container">
        <div className="distributor-card__top-container">
          <div className="distributor-card__content-wrapper">
            <div className="distributor-card__heading">
              <h2 className="distributor-card__title" id={id}>
                {name}
              </h2>
              {dynamicData.DistributorTypeDisplayName && !isMetal && (
                <p className="distributor-card__description">
                  {dynamicData.DistributorTypeDisplayName}
                </p>
              )}
            </div>
            <div className="distributor-card__address-wrapper">
              <p className="distributor-card__address">
                {dynamicData.FullAddress ? (
                  <>{dynamicData.FullAddress}</>
                ) : (
                  <>
                    {dynamicData.Address1}
                    {dynamicData.Address2 ? `, ${dynamicData.Address2}` : ''}
                    {dynamicData.City ? `, ${dynamicData.City}` : ''}
                    {dynamicData.State ? `, ${dynamicData.State}` : ''}
                  </>
                )}
              </p>
              <p className="distributor-card__get-directions">
                {!isMetal ? (
                  <a
                    href={buildGoogleMapsDirectionsURL(
                      dynamicData.Address1 as string
                    )}
                    className="link distributor-card__get-directions-link"
                    target="_blank"
                    data-layer={`[{"event_attributes": {"zip_code": ${dynamicData.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "get directions click"}]`}
                  >
                    <span>
                      {' '}
                      {milesFromSearchPoint ? `${trimmedMiles} mi` : ''} -{' '}
                      {getDirectionsLabel}
                    </span>
                    <svg viewBox="0, 0, 8, 14" role="presentation">
                      <use xlinkHref="#chevron-right" />
                    </svg>
                  </a>
                ) : (
                  <>
                    {!hideMiles && (
                      <span className="distributor-card__get-directions-label">
                        {milesFromSearchPoint ? `${trimmedMiles} mi` : ''}
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
          {!isMetal && (
            <div className="distributor-card__logo-wrapper">
              <img
                className="distributor-card__logo"
                src={distributorTypeImageSource}
                alt={distributorTypeDisplayName}
                loading="lazy"
                width={50}
                height={50}
              />
            </div>
          )}
        </div>
        <div
          className={`distributor-card__actions ${
            isMetal ? 'distributor-card__actions--metal' : ''
          }`}
        >
          {dynamicData.Phone ? (
            <a
              href={`tel:${dynamicData.Phone}`}
              className="distributor-card__actions-phone link--icon-left"
              data-layer={`[{"event_attributes": {"zip_code": ${dynamicData.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "phone click"}]`}
            >
              {formattedPhoneNumber}
              <svg
                className="red-phone-icon"
                viewBox="0 0 13 16"
                role="presentation"
              >
                <use xlinkHref="#red-phone-icon"></use>
              </svg>
            </a>
          ) : (
            ''
          )}
          {dynamicData.Website && !isMetal && (
            <a
              href={dynamicData.Website}
              className="distributor-card__actions-website"
              data-layer={`[{"event_attributes": {"zip_code": ${dynamicData.PostalCode}},"event": "result_click","event_category": "result click","event_action": "distributor locator","event_label": "website click"}]`}
            >
              {websiteLabel}
            </a>
          )}
          {dynamicData.ProfileUrl && isMetal && (
            <a
              href={dynamicData.ProfileUrl}
              className="link link--arrow distributor-card__actions-profile"
            >
              {profileLabel}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

DistributorCard.propTypes = {
  name: PropTypes.string.isRequired,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  milesFromSearchPoint: PropTypes.number.isRequired,
  customerId: PropTypes.string,
  distributorId: PropTypes.string.isRequired,
  distributorType: PropTypes.string.isRequired,
  dynamicData: PropTypes.shape({
    Address1: PropTypes.string,
    Address2: PropTypes.string,
    FullAddress: PropTypes.string,
    City: PropTypes.string,
    DistributorTypeDisplayName: PropTypes.string.isRequired,
    Fax: PropTypes.string,
    Phone: PropTypes.string,
    PostalCode: PropTypes.string.isRequired,
    ProductTypes: PropTypes.array,
    ProfileUrl: PropTypes.string,
    State: PropTypes.string,
    StoreNumber: PropTypes.string,
    Website: PropTypes.string
  }).isRequired,
  getDirectionsLabel: PropTypes.string,
  websiteLabel: PropTypes.string,
  profileLabel: PropTypes.string,
  imagesPath: PropTypes.string,
  isMetal: PropTypes.bool,
  hideMiles: PropTypes.bool
};

export default DistributorCard;
